import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, User, Lock, SlidersHorizontal, LogOut } from "@/components/icons";
import { signOut } from "@/auth/signOut";

type UserMenuProps = {
  displayName: string;
  email: string;
  /** When true, render only the avatar (used by the collapsed-sidebar UserPill). */
  compact?: boolean;
};

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? "?";
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function UserMenu({ displayName, email, compact }: UserMenuProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("common");

  async function handleSignout() {
    await signOut();
    navigate("/auth/signin", { replace: true });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={compact ? t("userMenu.menuLabel", { name: displayName }) : undefined}
        className={
          compact
            ? "flex w-full items-center justify-center rounded-md p-1 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            : "w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-muted text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        }
      >
        <Avatar className="h-7 w-7">
          <AvatarFallback className="text-xs bg-secondary text-secondary-foreground">
            {initialsOf(displayName)}
          </AvatarFallback>
        </Avatar>
        {!compact && (
          <>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{displayName}</div>
              <div className="text-xs text-muted-foreground truncate">{email}</div>
            </div>
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          </>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={() => navigate("/profile")}>
          <User className="me-2 h-4 w-4" /> {t("nav.profile")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/account")}>
          <Lock className="me-2 h-4 w-4" /> {t("nav.account")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/preferences")}>
          <SlidersHorizontal className="me-2 h-4 w-4" /> {t("nav.preferences")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignout}>
          <LogOut className="me-2 h-4 w-4" /> {t("nav.signOut")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
